import type { FiatFlow } from '@funkit/connect-core'
import { selectKycOutcome } from '@funkit/connect-core'
import { useEffect, useRef, useState } from 'react'

import type { FiatKycOutcomeScreenProps } from './FiatKycOutcomeScreen'

export type KycCompletion = FiatKycOutcomeScreenProps['outcome']

type FlowState = NonNullable<FiatFlow['stepResponse']>['state']

/** How long the acknowledgement withholds the verdict it already has. */
const HOLD_MS = 4000

/**
 * The acknowledgement owed to a finished identity form whose verdict is not
 * yet in: review, hold, or refusal. A form answered with the next step needs
 * none; that step is the acknowledgement.
 *
 * Derived in render, not an effect — the amount screen must not paint in the
 * gap. Capture is excluded: a provider surface reporting back is not a form.
 */
export function useKycCompletion(flow: FiatFlow): KycCompletion | undefined {
  const onForm = flow.page.screen === 'KYC_FORM'
  const completion = useRef<KycCompletion>(undefined)
  const wasOnForm = useRef(false)
  if (onForm) {
    wasOnForm.current = true
  } else if (wasOnForm.current) {
    // The form's own Close ends the flow, so the only way off it is the answer
    // to what was sent.
    wasOnForm.current = false
    completion.current = completionOutcome(flow.stepResponse?.state)
  }
  return completion.current
}

/**
 * The wait an acknowledgement makes before it gives up its verdict.
 *
 * - Counted from the moment the screen is up, not from when the answer landed:
 *   the verdict is often decided while another screen still owns the frame,
 *   and a wait spent behind one is a wait the buyer never had.
 */
export function useKycCompletionHold(): boolean {
  const [isHeld, setIsHeld] = useState(true)
  useEffect(() => {
    const timer = setTimeout(() => setIsHeld(false), HOLD_MS)
    return () => clearTimeout(timer)
  }, [])
  return isHeld
}

/**
 * - Answered with a price: nothing. The rail priced this purchase for this
 *   buyer, which it does not do for one it is still reading, so the form
 *   passed. `FiatKycOutcomeScreen` has no face for that — its outcomes are
 *   `review`, `hold` and `restricted` — and reporting a pass as "being
 *   reviewed" both misstates the verdict and strands the buyer, whose only
 *   way off that screen is the Close that ends the flow. The price itself is
 *   the acknowledgement.
 * - Off KYC entirely otherwise: the check is in, being read behind whatever
 *   came next.
 * - Correctable rejection: nothing. Something is left to try, and an
 *   acknowledgement would read as the end of the story.
 */
function completionOutcome(
  state: FlowState | undefined,
): KycCompletion | undefined {
  // Every non-KYC kind is exempt, not just QUOTE. A form answered with
  // SESSION_AUTH or PAYMENT is not a verdict, and acknowledging it as one
  // parks the buyer on a review screen whose only exit ends the flow.
  if (state?.kind !== 'KYC' || state.phase !== 'NO_ACTION_REQUIRED') {
    return undefined
  }
  if (state.reason === 'IN_REVIEW') {
    return 'review'
  }
  if (state.reason === 'ON_HOLD') {
    return 'hold'
  }
  return selectKycOutcome(state)?.outcome === 'hard' ? 'restricted' : undefined
}
